import { useCallback, useEffect, useRef, useState } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import getIsTWAMode from '../helpers/appMode_helper'
import { DISCOVERY_CODE_FEATURE_FLAG, isDiscoveryCodeVersionSupported } from '../helpers/discoveryCodeGate'
import {
  readDiscoveryCodeRecord,
  writeDiscoveryCodeRecord,
  hideDiscoveryCodeRecord,
  isDiscoveryCodeWindowExpired,
  DISCOVERY_CODE_WINDOW_MS,
  markDiscoveryCodeHiddenOnce,
  consumeDiscoveryCodeHiddenOnce,
  hasFiredDiscoveryCodeNaturalExpiry,
  markDiscoveryCodeNaturalExpiryFired,
} from '../helpers/discoveryCodeRecord'
import clearDiscoveryCodeNotification from '../helpers/clearDiscoveryCodeNotification'
import {
  trackDiscoveryCodeHomeCardImpression,
  trackDiscoveryCodeHideConfirmed,
  trackDiscoveryCodeNaturalExpiry,
  trackDiscoveryCodeSessionReplaced,
  trackDiscoveryCodeRescanRestored,
  trackDiscoveryCodeLiveActivityTapped,
  trackDiscoveryCodeLiveActivityUnavailable,
} from '../helpers/discoveryCodeAnalytics'
import {
  getTwaNotificationsEnabled,
  wasOpenedFromDiscoveryCodeNotification,
  hasFiredLiveActivityUnavailable,
  markLiveActivityUnavailableFired,
} from '../helpers/twaNotifications'
import { getCenterSessionForUser } from '../services/discoveryCenterSessionService'
import useReactQuery from './useReactQuery'
import { qk } from './queryKeys'

const fireOnce = (firedRef, track) => {
  if (firedRef.current) return
  firedRef.current = true
  track()
}

/**
 * Returns the stored record for `sessionId`, replacing any record left over from a different session.
 * @param {() => void} onReplaced called before a record for another session is overwritten
 */
function syncRecordToSession(sessionId, onReplaced) {
  const stored = readDiscoveryCodeRecord()
  if (stored?.sessionId === sessionId) return stored

  if (stored) {
    onReplaced()
    consumeDiscoveryCodeHiddenOnce(stored.sessionId)
  }
  const record = { sessionId, firstSeenAt: Date.now(), hidden: false }
  writeDiscoveryCodeRecord(record)
  return record
}

/**
 * Drives the Discovery Code card on the All Stories page.
 * @param {{ enabled?: boolean }} [options] pass `enabled: false` while the card cannot be shown (e.g. a person
 * is selected) so the Center Session is not fetched and no impression analytics fire.
 * @returns {{ code: string | null, onHide: () => void }} `code` is null whenever nothing should render.
 */
export default function useDiscoveryCodeCard({ enabled = true } = {}) {
  const isTWAMode = getIsTWAMode()
  const featureFlagOn = useFeatureFlag(DISCOVERY_CODE_FEATURE_FLAG).isOn
  const versionSupported = isDiscoveryCodeVersionSupported()
  const gatesPass = enabled && isTWAMode && featureFlagOn && versionSupported

  const { data } = useReactQuery({
    queryKey: qk.centerSession(),
    queryFn: getCenterSessionForUser,
    enabled: gatesPass,
    hideErrorOverlay: true,
    staleTime: 60000,
    retry: false, // 404 (no Center Session) is the normal case, not a transient failure
  })

  const [code, setCode] = useState(null)
  // Bumped when the display window lapses so the card re-evaluates (and expires) while the page stays mounted.
  const [expiryTick, setExpiryTick] = useState(0)
  const sessionIdRef = useRef(null)

  const firedImpressionRef = useRef(false)
  const firedNaturalExpiryRef = useRef(false)
  const firedSessionReplacedRef = useRef(false)
  const firedRescanRestoredRef = useRef(false)
  const firedLiveActivityTappedRef = useRef(false)
  const firedLiveActivityUnavailableRef = useRef(false)

  useEffect(() => {
    const fetched = data
    if (!gatesPass || !fetched?.sessionId) {
      setCode(null)
      return
    }

    const record = syncRecordToSession(fetched.sessionId, () =>
      fireOnce(firedSessionReplacedRef, trackDiscoveryCodeSessionReplaced)
    )
    sessionIdRef.current = fetched.sessionId

    if (isDiscoveryCodeWindowExpired(record)) {
      if (!hasFiredDiscoveryCodeNaturalExpiry(record.sessionId)) {
        fireOnce(firedNaturalExpiryRef, () => {
          markDiscoveryCodeNaturalExpiryFired(record.sessionId)
          trackDiscoveryCodeNaturalExpiry()
        })
      }
      setCode(null)
      return
    }

    if (record.hidden) {
      setCode(null)
      return
    }

    if (consumeDiscoveryCodeHiddenOnce(fetched.sessionId)) {
      fireOnce(firedRescanRestoredRef, trackDiscoveryCodeRescanRestored)
    }

    setCode(fetched.code)
    fireOnce(firedImpressionRef, trackDiscoveryCodeHomeCardImpression)
  }, [gatesPass, data, expiryTick])

  useEffect(() => {
    const record = readDiscoveryCodeRecord()
    if (!code || !record) return undefined

    const remainingMs = record.firstSeenAt + DISCOVERY_CODE_WINDOW_MS - Date.now()
    const expiryTimeout = setTimeout(() => setExpiryTick((tick) => tick + 1), remainingMs)
    return () => clearTimeout(expiryTimeout)
  }, [code])

  useEffect(() => {
    if (!gatesPass) return

    if (!firedLiveActivityTappedRef.current && wasOpenedFromDiscoveryCodeNotification()) {
      firedLiveActivityTappedRef.current = true
      trackDiscoveryCodeLiveActivityTapped()
    }

    if (
      !firedLiveActivityUnavailableRef.current &&
      !hasFiredLiveActivityUnavailable() &&
      getTwaNotificationsEnabled() === false
    ) {
      firedLiveActivityUnavailableRef.current = true
      markLiveActivityUnavailableFired()
      trackDiscoveryCodeLiveActivityUnavailable('authorization_disabled')
    }
  }, [gatesPass])

  const onHide = useCallback(() => {
    hideDiscoveryCodeRecord()
    if (sessionIdRef.current) markDiscoveryCodeHiddenOnce(sessionIdRef.current)
    clearDiscoveryCodeNotification()
    trackDiscoveryCodeHideConfirmed()
    setCode(null)
  }, [])

  return { code, onHide }
}
